import { ReactElement, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { Button, Modal } from "@wfp/ui"
import { iconCheckmark, iconClose, iconVisibilityOff, iconVisibilityOn } from "@wfp/icons"

import { useGetReportObsQuery } from "services/marketprices/commodityPrice"
import { ReportStatuses } from "../../constants/prices"
import { ObservationStatus } from "../../interfaces/markets"

import DataUploadProvider from "../../providers/DataUploadContext"
import { useChangeReportStatusMutation } from "../../services/marketprices/reports"

import Validation from "../../components/Markets/ExcelDataUpload/Validation/Validation"

const DataReviewPage = (): ReactElement => {
  const navigate = useNavigate()
  const { rId } = useParams<{ rId: string }>()

  const [modalOpen, setModalOpen] = useState<{
    type: "approve" | "reject" | "reject-new-data" | "approve-old-data" | null
    isOpen: boolean
  }>({
    type: null,
    isOpen: false,
  })

  const { data } = useGetReportObsQuery(Number(rId) || 0, { skip: !rId })
  const [changeReportStatus, { isLoading: isLoadingStatus }] = useChangeReportStatusMutation()

  const closeModal = (): void => {
    setModalOpen({ type: null, isOpen: false })
    navigate("/market-prices/reports")
  }

  const newObservations: boolean = useMemo(() => {
    if (!data) return false

    return (
      data.some(item => item.observationStatusId !== ObservationStatus.APPROVED) &&
      data.some(item => item.observationStatusId === ObservationStatus.APPROVED)
    )
  }, [data])

  return (
    <DataUploadProvider>
      <div className="px-8">
        <h1 className="text-2xl font-bold mb-3">Observations Review</h1>

        <Validation />

        {!!data?.length && (
          <div className="flex justify-end mt-4 gap-x-4">
            <Button
              onClick={() => setModalOpen({ type: newObservations ? "reject-new-data" : "reject", isOpen: true })}
              icon={iconClose}
              iconReverse
              className="text-sm"
              kind="danger"
              disabled={isLoadingStatus}
            >
              Reject {newObservations ? "only the new observations" : ""}
            </Button>

            <Button
              onClick={() => setModalOpen({ type: "approve", isOpen: true })}
              icon={iconCheckmark}
              iconReverse
              className="text-sm"
              disabled={
                isLoadingStatus ||
                !data?.some(
                  item =>
                    item.observationStatusId === ObservationStatus.VALID ||
                    item.observationStatusId === ObservationStatus.APPROVED,
                )
              }
            >
              Approve
            </Button>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen.isOpen}
        modalHeading={modalOpen.type?.includes("approve") ? "Approve" : "Reject"}
        primaryButtonText={modalOpen.type?.includes("approve") ? "Approve" : "Reject"}
        secondaryButtonText="Cancel"
        lazyLoad
        danger={modalOpen.type === "reject" || modalOpen.type === "reject-new-data"}
        passiveModal={modalOpen.type?.includes("approve")}
        onRequestClose={() => setModalOpen({ type: null, isOpen: false })}
        onRequestSubmit={async () => {
          if (modalOpen.type === "reject-new-data") {
            setModalOpen({ type: "approve-old-data", isOpen: true })
            return
          }
          await changeReportStatus({ id: Number(rId), status: ReportStatuses.Rejected })
          closeModal()
        }}
      >
        {modalOpen.type?.includes("approve") && (
          <div>
            {modalOpen.type === "approve-old-data" && (
              <p className="mb-1">
                The report contains observations that were previously approved. <br />
                Please confirm their visibility status.
              </p>
            )}
            <p className="mb-2">Select the visibility of the report</p>
            <div className="flex gap-4 justify-between">
              <Button
                onClick={() => {
                  if (modalOpen.type === "approve-old-data") {
                    changeReportStatus({
                      id: Number(rId),
                      status: ReportStatuses.Rejected,
                      previousApprovedStatus: ReportStatuses.ApprovedInternal,
                    })
                  } else changeReportStatus({ id: Number(rId), status: ReportStatuses.ApprovedInternal })
                  closeModal()
                }}
                icon={iconVisibilityOff}
                iconReverse
                kind="tertiary"
              >
                Internal
              </Button>

              <Button
                onClick={() => {
                  if (modalOpen.type === "approve-old-data") {
                    changeReportStatus({
                      id: Number(rId),
                      status: ReportStatuses.Rejected,
                      previousApprovedStatus: ReportStatuses.Approved,
                    })
                  } else changeReportStatus({ id: Number(rId), status: ReportStatuses.Approved })
                  closeModal()
                }}
                icon={iconVisibilityOn}
                iconReverse
                kind="primary"
              >
                Public
              </Button>
            </div>
          </div>
        )}

        {modalOpen.type === "reject-new-data" && <p>The new observations will be rejected.</p>}

        {modalOpen.type === "reject" && <p>Are you sure you want to reject the selected report?</p>}
      </Modal>
    </DataUploadProvider>
  )
}

export default DataReviewPage
